/** The signed-out screen. Email and password; accounts are created by an administrator. */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { Field, Notice } from "@/components/ui";

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(params.get("error") ?? undefined);
  const [ssoAvailable, setSsoAvailable] = useState(false);

  useEffect(() => {
    endpoints
      .ready()
      .then((response) => setSsoAvailable(response.checks.sso === "configured"))
      .catch(() => setSsoAvailable(false));
  }, []);

  const continueWithMicrosoft = () => {
    const next = window.location.pathname + window.location.search;
    window.location.href = `/api/auth/sso/login?next=${encodeURIComponent(next)}`;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.signIn(email, password);
      onSignedIn();
    } catch (caught) {
      setProblem(
        caught instanceof ApiError ? caught.message : (caught as Error).message,
      );
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <div className="signin__mark" aria-hidden="true">
          D
        </div>
        <h1>Delivery Hub</h1>
        <p className="signin__lede">Run Human Data delivery scripts without a laptop.</p>

        {problem && (
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <Notice tone="danger">{problem}</Notice>
          </div>
        )}

        {ssoAvailable && (
          <>
            <button
              type="button"
              className="button"
              style={{ width: "100%", marginBottom: 16 }}
              onClick={continueWithMicrosoft}
              disabled={busy}
            >
              Continue with Microsoft
            </button>
            <div className="signin__divider" role="separator">
              <span>or</span>
            </div>
          </>
        )}

        <div className="stack" style={{ gap: 12, textAlign: "left" }}>
          <Field label="Email" htmlFor="signin-email">
            <input
              id="signin-email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              required
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="signin-password">
            <input
              id="signin-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <button
            type="submit"
            className="button button--primary"
            style={{ width: "100%", alignSelf: "stretch" }}
            disabled={busy}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>

        <p className="faint" style={{ marginTop: 18, marginBottom: 0, fontSize: 12.5 }}>
          Accounts are created by an administrator. If you cannot get in, ask them to
          invite you or send a fresh link.
        </p>
      </form>
    </div>
  );
}
